import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { FC, Suspense, useCallback, useEffect, useState } from 'react';
import Joyride, { CallBackProps } from 'react-joyride';
import { Outlet } from 'react-router-dom';

import { useHeaderState } from '../../contexts/HeaderContext';

import {
  aiAssistantJoyrideSteps,
  jaJoyrideLocale,
} from '../../domain/reactJoyride/joyrideSteps';

import { HEADER_HEIGHT } from '../../consistents';
import { gaClickEvent } from '../../utils/googleAnalytics';
import { mergeSxProps } from '../../utils/mergeSxProps';
import { PlaceholderLoading } from '../atoms/PlaceholderLoading/PlaceholderLoading';
import { ScreenOnly } from '../atoms/ScreenOnly/ScreenOnly';
import { AiAssistant } from '../organisms/AiAssistant/AiAssistant';
import { GlobalHeader } from '../organisms/GlobalHeader/GlobalHeader';
import { GlobalNavigation } from '../organisms/GlobalNavigation/GlobalNavigation';

type PresenterProps = {
  isOpenMenu: boolean;
  isOpenAiAssistant: boolean;
  onToggleAiAssistant: (isOpen: boolean) => void;
};

const drawerWidth = 260;

const AiAssistantWidth = 400;

const styles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    width: '100%',
    height: '100dvh',
  },
  toolbarSpace: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: (theme) => theme.spacing(0, 1),
    minHeight: `${HEADER_HEIGHT}px`,
  },
  contents: {
    padding: 0,
    flex: 1,
    minWidth: 0,
  },
  drawer: {
    width: `${drawerWidth}px`,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: `${drawerWidth}px`,
    transition: (theme) =>
      theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
  },
  drawerClose: {
    transition: (theme) =>
      theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    overflowX: 'hidden',
    width: 57,
  },
};

const GenericLayoutPresenter: FC<PresenterProps> = (props) => {
  const { isOpenMenu, isOpenAiAssistant, onToggleAiAssistant } = props;

  // コンポーネント関数内の先頭に追加
  const [showGuide, setShowGuide] = useState(false);

  // useCallbackを使用して関数をメモ化
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, status, index, type, lifecycle } = data;

      if (action === 'start') {
        // ステップ1のときに、ステップ2のJoyrideのガイド判定がズレる事があるためAIアシスタントをあらかじめ開く
        onToggleAiAssistant(true);
      }

      // 次への場合のGAイベントを送信
      if (
        action === 'next' &&
        index === 0 &&
        status === 'running' &&
        lifecycle === 'complete'
      ) {
        gaClickEvent('AIアシスタントガイド_ステップ1_進む');
      }
      // 戻る場合のGAイベントを送信
      if (
        action === 'prev' &&
        index === 1 &&
        status === 'running' &&
        lifecycle === 'complete'
      ) {
        gaClickEvent('AIアシスタントガイド_ステップ2_戻る');
      }

      // ガイドを閉じる
      if (status === 'finished' || status === 'skipped' || action === 'close') {
        localStorage.setItem('aiGuideSeen', 'true');
        setShowGuide(false);
        onToggleAiAssistant(false);
        // GAイベントを送信
        if (
          action === 'close' &&
          status === 'running' &&
          type === 'step:after' &&
          index === 0
        ) {
          gaClickEvent('AIアシスタントガイド_ステップ1_閉じる');
        } else if (
          action === 'close' &&
          status === 'finished' &&
          type === 'tour:end' &&
          index === 1
        ) {
          gaClickEvent('AIアシスタントガイド_ステップ2_閉じる');
        }

        if (action === 'next' && status === 'finished' && type === 'tour:end') {
          gaClickEvent('AIアシスタントガイド_終了');
        }
      }
    },
    [onToggleAiAssistant]
  );

  useEffect(() => {
    const seen = localStorage.getItem('aiGuideSeen');
    // ローカルストレージで判定(初回のみ表示)
    if (!seen) {
      // DOMの対象要素がレンダリングされる前に開始すると失敗することがあるため少し遅延を入れる
      setTimeout(() => {
        setShowGuide(true);
      }, 300);
    }
  }, []);

  return (
    <>
      <Joyride
        run={showGuide}
        steps={aiAssistantJoyrideSteps}
        disableScrollParentFix={true}
        continuous
        disableOverlayClose
        styles={{
          options: { zIndex: 10000 },
          tooltip: {
            maxWidth: 500,
            width: 'auto',
          },
        }}
        callback={handleJoyrideCallback}
        locale={jaJoyrideLocale}
      />
      <Box sx={styles.root}>
        <ScreenOnly>
          <Drawer
            variant="permanent"
            sx={mergeSxProps(
              styles.drawer,
              isOpenMenu ? styles.drawerOpen : styles.drawerClose
            )}
            slotProps={{
              paper: {
                sx: mergeSxProps(
                  styles.drawer,
                  isOpenMenu ? styles.drawerOpen : styles.drawerClose
                ),
              },
            }}
          >
            <GlobalNavigation
              onToggleAiAssistant={onToggleAiAssistant}
              isOpenAiAssistant={isOpenAiAssistant}
            />
          </Drawer>
        </ScreenOnly>
        <Box
          component="main"
          sx={{
            ...styles.contents,
            maxHeight: '100vh',
            overflowY: 'scroll',
          }}
        >
          <Suspense
            fallback={
              <>
                <GlobalHeader pageTitle={undefined} />
                <PlaceholderLoading />
              </>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
        {/* isOpenAiAssistantに応じて表示制御する */}
        <Drawer
          anchor="right"
          // memo: スマホの場合bottomで制御すると良さそう
          open={isOpenAiAssistant}
          variant="persistent"
          sx={{
            width: isOpenAiAssistant ? AiAssistantWidth : 0,
            transition: 'width 0.15s ease-in-out',
            flexShrink: 0,
            // GlobalHeaderのzIndexがtheme.zIndex.drawer+1なので、+2にしている
            zIndex: 200000, //ChatPlusのzIndexが100001なので、それよりも大きなものにしておく。数値変動があるかは不明だが、余裕を持たせておく
            '& .MuiDrawer-paper': {
              width: AiAssistantWidth,
            },
          }}
        >
          <AiAssistant onClose={() => onToggleAiAssistant(false)} />
        </Drawer>
      </Box>
    </>
  );
};
export const GenericLayout: FC = () => {
  const { isOpenMenu } = useHeaderState();
  const [isOpenAiAssistant, setIsOpenAiAssistant] = useState(false);

  // チャットエリアの開閉状態を管理する関数
  const handleToggleAiAssistant = (isOpen: boolean) =>
    setIsOpenAiAssistant(isOpen);

  return (
    <GenericLayoutPresenter
      isOpenMenu={isOpenMenu}
      isOpenAiAssistant={isOpenAiAssistant}
      onToggleAiAssistant={handleToggleAiAssistant}
    />
  );
};
